/*
 * DeVogue size finder.
 *
 * Measures a foot from a photograph of a traced outline on a sheet of A4, and
 * maps the length to a DeVogue size.
 *
 * The geometry is a four-corner homography, not a single-edge scale factor.
 * That matters: a tilted camera scales differently along different axes, so a
 * scale derived from one paper edge and then applied to a distance running in
 * another direction is simply wrong, with no symptom. Rectifying the paper
 * plane from all four corners removes camera angle from the problem entirely.
 *
 * Everything below runs on the customer's phone. No photograph is uploaded.
 *
 * The maths section touches no DOM so it can be exercised from Node; see
 * tools/size-finder-selftest.mjs.
 */
(function (root) {
  'use strict';

  // ----------------------------------------------------------------- config

  /*
   * The published DeVogue chart, as shown on /pages/size-chart and /pages/faqs.
   * Both state that the figure is the foot length from heel to longest toe, so
   * NO comfort allowance is added before lookup: the allowance lives in the
   * shoe last, not in the chart. Adding one here would push nearly every
   * customer a full size up, because the mean gap between our sizes is 0.85 cm.
   */
  var SIZE_CHART = [
    { size: 6, cm: 23.7, eu: 39 },
    { size: 7, cm: 24.6, eu: 40 },
    { size: 8, cm: 25.4, eu: 41 },
    { size: 9, cm: 26.2, eu: 42 },
    { size: 10, cm: 27.1, eu: 43 },
    { size: 11, cm: 27.9, eu: 44 },
    { size: 12, cm: 28.8, eu: 45 }
  ];

  /*
   * Whether a chart figure is the NOMINAL foot length for that size, or the
   * MAXIMUM length that still fits it. The wording on the page reads as
   * nominal, so 'nearest' is the default. If it turns out to be a maximum,
   * change this one word and every recommendation shifts by half a size.
   */
  var LOOKUP_MODE = 'nearest'; // 'nearest' | 'roundup'

  /* Realistic 95% error of the whole method, in cm. */
  var UNCERTAINTY_CM = 0.3;

  /*
   * How close to the boundary between two sizes a measurement has to fall
   * before we say so. Roughly one standard deviation, NOT the 95% figure
   * above: our sizes are only 0.85 cm apart, so a 0.3 cm threshold flags a
   * band 0.6 cm wide and about seven customers in ten would be told they are
   * between sizes, which turns a genuinely useful warning into noise. It also
   * read plainly wrong in testing, calling 26.0 cm "between 8 and 9" when it
   * sits 0.2 cm from a 9 and 0.6 cm from an 8.
   *
   * At 0.15 the note appears for roughly a third of measurements, which is an
   * honest reflection of measuring a 0.85 cm step when no half sizes exist.
   */
  var BOUNDARY_CM = 0.15;

  /* Candidate reference sheets, long/short ratio. The customer declares which
   * one they used; these ratios are what that declaration is checked against. */
  var PAPERS = [
    { id: 'a4', label: 'A4', short: 21.0, long: 29.7 },
    { id: 'letter', label: 'US Letter', short: 21.59, long: 27.94 },
    { id: 'legal', label: 'US Legal', short: 21.59, long: 35.56 }
  ];
  PAPERS.forEach(function (p) { p.ratio = p.long / p.short; });

  var LIMITS = {
    maxTiltDeg: 35,       // beyond this, ask for a flatter shot
    /*
     * How far the measured sheet ratio may sit from the declared sheet.
     * Measured against synthetic photographs: 8 px of corner error moves the
     * ratio by about 4%, so rejecting at 4% would refuse sloppy but perfectly
     * usable placements. 6% still catches the case this guard exists for, a
     * corner put on the wrong feature, and still separates A4 (1.414) from US
     * Letter (1.294), which is 8.5% away.
     */
    paperTolerance: 0.06,
    paperWarn: 0.035,     // above this, say the placement looks imprecise
    minFootCm: 20,
    maxFootCm: 34,
    maxFeetDiffCm: 1.0,
    workingEdgePx: 2400   // cap the working canvas: iOS blanks oversized ones
  };

  // ------------------------------------------------------------ linear algebra

  /* Gauss-Jordan with partial pivoting. Returns null on a singular system
   * rather than NaNs, so callers can report "these corners are degenerate"
   * instead of silently producing a nonsense measurement. */
  function solveLinear(M, rhs, n) {
    var a = [];
    for (var i = 0; i < n; i++) a.push(M[i].slice().concat([rhs[i]]));
    for (var col = 0; col < n; col++) {
      var piv = col;
      for (var r = col + 1; r < n; r++) {
        if (Math.abs(a[r][col]) > Math.abs(a[piv][col])) piv = r;
      }
      if (Math.abs(a[piv][col]) < 1e-12) return null;
      var tmp = a[col]; a[col] = a[piv]; a[piv] = tmp;
      for (var r2 = 0; r2 < n; r2++) {
        if (r2 === col) continue;
        var f = a[r2][col] / a[col][col];
        if (f === 0) continue;
        for (var c = col; c <= n; c++) a[r2][c] -= f * a[col][c];
      }
    }
    var x = [];
    for (var k = 0; k < n; k++) x.push(a[k][n] / a[k][k]);
    return x;
  }

  /* Homography mapping four source points to four destination points.
   * Exact for four correspondences: one 8x8 solve, no iteration, no library. */
  function homography(src, dst) {
    var M = [], b = [];
    for (var i = 0; i < 4; i++) {
      var x = src[i][0], y = src[i][1], u = dst[i][0], v = dst[i][1];
      M.push([x, y, 1, 0, 0, 0, -x * u, -y * u]); b.push(u);
      M.push([0, 0, 0, x, y, 1, -x * v, -y * v]); b.push(v);
    }
    var h = solveLinear(M, b, 8);
    if (!h) return null;
    return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
  }

  function applyH(H, p) {
    var w = H[6] * p[0] + H[7] * p[1] + H[8];
    if (!isFinite(w) || Math.abs(w) < 1e-12) return null;
    return [
      (H[0] * p[0] + H[1] * p[1] + H[2]) / w,
      (H[3] * p[0] + H[4] * p[1] + H[5]) / w
    ];
  }

  function dist(a, b) { return Math.hypot(a[0] - b[0], a[1] - b[1]); }

  // -------------------------------------------------------- quad geometry

  /* Order four points as a consistent ring starting from the corner nearest
   * the image origin. Sorting by angle around the centroid also guarantees the
   * ring is non-self-intersecting, which the convexity test then relies on. */
  function orderCorners(pts) {
    var cx = 0, cy = 0;
    pts.forEach(function (p) { cx += p[0]; cy += p[1]; });
    cx /= pts.length; cy /= pts.length;
    var ring = pts.slice().sort(function (a, b) {
      return Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx);
    });
    var best = 0, bestScore = Infinity;
    for (var i = 0; i < ring.length; i++) {
      var s = ring[i][0] + ring[i][1];
      if (s < bestScore) { bestScore = s; best = i; }
    }
    return ring.slice(best).concat(ring.slice(0, best));
  }

  function isConvex(c) {
    var sign = 0;
    for (var i = 0; i < 4; i++) {
      var a = c[i], b = c[(i + 1) % 4], d = c[(i + 2) % 4];
      var cross = (b[0] - a[0]) * (d[1] - b[1]) - (b[1] - a[1]) * (d[0] - b[0]);
      if (Math.abs(cross) < 1e-9) return false;
      var s = cross > 0 ? 1 : -1;
      if (sign === 0) sign = s;
      else if (s !== sign) return false;
    }
    return true;
  }

  /*
   * Recover what the quadrilateral implies about the sheet and the camera.
   *
   * A four-point homography always fits exactly, so there is no residual to
   * check and no free error signal. The signal has to come from the SHAPE of
   * the quad instead. Treating the quad as a rectangle seen by a pinhole camera
   * with a centred principal point, the orthogonality of the two in-plane axes
   * gives the focal length, and the ratio of the axis lengths then gives the
   * sheet's true aspect ratio. Two useful things fall out:
   *
   *   - a quad that cannot be a rectangle under ANY camera angle is rejected,
   *     which catches mis-placed corners;
   *   - the recovered aspect is checked against the sheet the customer said
   *     they used, so someone who reached for US Letter is told, rather than
   *     measured 6% short in silence.
   */
  function analyseQuad(corners, imgW, imgH, expectedPaperId) {
    if (!corners || corners.length !== 4) return { ok: false, reason: 'need_four_corners' };
    var c = orderCorners(corners);
    if (!isConvex(c)) return { ok: false, reason: 'not_convex' };

    // Unit square -> image. Unit x runs c0->c1, unit y runs c0->c3.
    var H = homography([[0, 0], [1, 0], [1, 1], [0, 1]], c);
    if (!H) return { ok: false, reason: 'degenerate' };

    var px = imgW / 2, py = imgH / 2;              // principal point
    var priorF = 1.2 * Math.max(imgW, imgH);       // typical phone main camera

    var A1 = H[0] - px * H[6], B1 = H[3] - py * H[6];
    var A2 = H[1] - px * H[7], B2 = H[4] - py * H[7];

    /*
     * How much perspective is actually present. H[6] and H[7] are the
     * fractional change in scale across the sheet, so 0.10 means the far edge
     * is imaged about 10% smaller than the near one.
     *
     * This gate matters more than it looks. Focal length is recovered from the
     * orthogonality of the two in-plane axes, which divides by H[6]*H[7]. In a
     * flat overhead shot, exactly what we ask the customer for, both approach
     * zero and that is a ratio of two vanishing quantities: corner noise of a
     * couple of pixels flips it negative and the sheet gets rejected as "not a
     * rectangle". Measured before this guard existed, a PERFECT overhead
     * photograph was rejected about half the time, and tilting the phone made
     * it work better. Precisely backwards.
     *
     * The saving grace is that when perspective is weak the aspect ratio stops
     * depending on f at all (the f terms cancel as H[6], H[7] go to zero), so
     * falling back to a nominal focal length costs nothing.
     */
    var persp = Math.max(Math.abs(H[6]), Math.abs(H[7]));
    var WEAK = 0.05, STRONG = 0.15;

    var f = priorF, fRecovered = false;
    var denom = H[6] * H[7];
    if (persp > WEAK && Math.abs(denom) > 1e-14) {
      var f2 = -(A1 * A2 + B1 * B2) / denom;
      if (f2 > 0) {
        var cand = Math.sqrt(f2);
        // A plausible phone focal length. Outside this, the rectangle
        // assumption is not holding, so keep the prior rather than trust it.
        if (cand > 0.4 * Math.max(imgW, imgH) && cand < 6 * Math.max(imgW, imgH)) {
          f = cand; fRecovered = true;
        }
      } else if (persp > STRONG) {
        // Strong perspective and still no solution: this really is not the
        // image of a rectangle, so a corner is in the wrong place.
        return { ok: false, reason: 'not_a_rectangle', corners: c, persp: persp };
      }
    }

    var n1 = [A1 / f, B1 / f, H[6]];
    var n2 = [A2 / f, B2 / f, H[7]];
    var L1 = Math.hypot(n1[0], n1[1], n1[2]);
    var L2 = Math.hypot(n2[0], n2[1], n2[2]);
    if (!(L1 > 0) || !(L2 > 0)) return { ok: false, reason: 'degenerate', corners: c };

    var aspect = L2 / L1;                    // physical height / width
    var ratio = aspect >= 1 ? aspect : 1 / aspect;

    // Plane normal in camera coordinates, hence how far off flat the shot is.
    var u1 = [n1[0] / L1, n1[1] / L1, n1[2] / L1];
    var u2 = [n2[0] / L2, n2[1] / L2, n2[2] / L2];
    var n3 = [
      u1[1] * u2[2] - u1[2] * u2[1],
      u1[2] * u2[0] - u1[0] * u2[2],
      u1[0] * u2[1] - u1[1] * u2[0]
    ];
    var n3len = Math.hypot(n3[0], n3[1], n3[2]) || 1;
    var tiltDeg = Math.acos(Math.min(1, Math.abs(n3[2]) / n3len)) * 180 / Math.PI;

    /*
     * The sheet is DECLARED, not guessed, and the measured ratio only checks
     * that declaration.
     *
     * Picking the nearest paper from the measured ratio looks tidier and is a
     * trap. The ratio estimate is noisy, and A4 (1.414) sits close enough to US
     * Legal (1.647) that a sloppily placed corner could tip the choice. That
     * failure is silent and costs 20% on every measurement, which is worse than
     * any refusal. Getting the customer's confirmation is cheap; measuring the
     * wrong sheet is not.
     */
    var declared = null;
    var wantId = expectedPaperId || 'a4';
    PAPERS.forEach(function (p) { if (p.id === wantId) declared = p; });
    if (!declared) declared = PAPERS[0];

    var ratioError = Math.abs(ratio - declared.ratio) / declared.ratio;
    if (ratioError > LIMITS.paperTolerance) {
      var looksLike = null, bestErr = Infinity;
      PAPERS.forEach(function (p) {
        var e = Math.abs(ratio - p.ratio) / p.ratio;
        if (e < bestErr) { bestErr = e; looksLike = p; }
      });
      return {
        ok: false, reason: 'paper_mismatch', corners: c, ratio: ratio,
        tiltDeg: tiltDeg, declared: declared, ratioError: ratioError,
        looksLike: bestErr < LIMITS.paperTolerance ? looksLike : null,
        persp: persp
      };
    }

    return {
      ok: true, corners: c, paper: declared, ratio: ratio, ratioError: ratioError,
      aspect: aspect, focal: f, focalRecovered: fRecovered, tiltDeg: tiltDeg,
      persp: persp, portrait: aspect >= 1
    };
  }

  /* Image pixels to centimetres on the sheet. */
  function measurementHomography(analysis) {
    var p = analysis.paper;
    var w = analysis.portrait ? p.short : p.long;
    var h = analysis.portrait ? p.long : p.short;
    return homography(analysis.corners, [[0, 0], [w, 0], [w, h], [0, h]]);
  }

  /* Centimetres back to image pixels, for placing initial handles. */
  function inverseMeasurementHomography(analysis) {
    var p = analysis.paper;
    var w = analysis.portrait ? p.short : p.long;
    var h = analysis.portrait ? p.long : p.short;
    return homography([[0, 0], [w, 0], [w, h], [0, h]], analysis.corners);
  }

  // ------------------------------------------------------------- measurement

  function measure(corners, heel, toe, imgW, imgH, paperId) {
    var analysis = analyseQuad(corners, imgW, imgH, paperId);
    if (!analysis.ok) return { ok: false, reason: analysis.reason, analysis: analysis };

    var H = measurementHomography(analysis);
    if (!H) return { ok: false, reason: 'degenerate', analysis: analysis };

    var a = applyH(H, heel), b = applyH(H, toe);
    if (!a || !b) return { ok: false, reason: 'degenerate', analysis: analysis };

    var cm = dist(a, b);
    var warnings = [];
    if (analysis.tiltDeg > LIMITS.maxTiltDeg) warnings.push('tilt');
    if (analysis.ratioError > LIMITS.paperWarn) warnings.push('corner_precision');
    if (analysis.paper.id !== 'a4') warnings.push('paper_' + analysis.paper.id);

    if (!(cm > LIMITS.minFootCm && cm < LIMITS.maxFootCm)) {
      return {
        ok: false, reason: 'implausible_length', cm: cm,
        analysis: analysis, warnings: warnings
      };
    }
    // Off the sheet entirely means a corner or a point is misplaced.
    var pp = analysis.paper;
    var lim = [analysis.portrait ? pp.short : pp.long, analysis.portrait ? pp.long : pp.short];
    var pad = 1.5;
    if (a[0] < -pad || a[1] < -pad || b[0] < -pad || b[1] < -pad ||
        a[0] > lim[0] + pad || b[0] > lim[0] + pad ||
        a[1] > lim[1] + pad || b[1] > lim[1] + pad) {
      warnings.push('off_sheet');
    }

    return {
      ok: true, cm: cm, heelCm: a, toeCm: b,
      analysis: analysis, warnings: warnings
    };
  }

  // ----------------------------------------------------------- size lookup

  function recommend(cm) {
    var first = SIZE_CHART[0], last = SIZE_CHART[SIZE_CHART.length - 1];
    var halfStep = (SIZE_CHART[1].cm - SIZE_CHART[0].cm) / 2;

    if (cm < first.cm - halfStep) {
      return { ok: false, reason: 'below_range', cm: cm, nearest: first };
    }
    if (cm > last.cm + halfStep) {
      return { ok: false, reason: 'above_range', cm: cm, nearest: last };
    }

    var nearest = SIZE_CHART[0], bestD = Infinity, idx = 0;
    SIZE_CHART.forEach(function (e, i) {
      var d = Math.abs(e.cm - cm);
      if (d < bestD) { bestD = d; nearest = e; idx = i; }
    });

    // The neighbour on the side the measurement actually leans towards.
    var lower = null, upper = null;
    for (var i = 0; i < SIZE_CHART.length - 1; i++) {
      if (cm >= SIZE_CHART[i].cm && cm <= SIZE_CHART[i + 1].cm) {
        lower = SIZE_CHART[i]; upper = SIZE_CHART[i + 1];
        break;
      }
    }

    var between = false, midpoint = null;
    if (lower && upper) {
      midpoint = (lower.cm + upper.cm) / 2;
      between = Math.abs(cm - midpoint) < BOUNDARY_CM;
    }

    var chosen = nearest;
    if (LOOKUP_MODE === 'roundup' && upper) chosen = upper;
    // Store rule, already stated in the loafer FAQ: between sizes, size up.
    if (between && upper) chosen = upper;

    return {
      ok: true, cm: cm, size: chosen.size, eu: chosen.eu,
      nearest: nearest, between: between, lower: lower, upper: upper,
      midpoint: midpoint, mode: LOOKUP_MODE
    };
  }

  var Maths = {
    SIZE_CHART: SIZE_CHART, PAPERS: PAPERS, LIMITS: LIMITS,
    UNCERTAINTY_CM: UNCERTAINTY_CM, BOUNDARY_CM: BOUNDARY_CM,
    solveLinear: solveLinear, homography: homography, applyH: applyH,
    orderCorners: orderCorners, isConvex: isConvex, analyseQuad: analyseQuad,
    measurementHomography: measurementHomography,
    inverseMeasurementHomography: inverseMeasurementHomography,
    measure: measure, recommend: recommend
  };

  root.SizeFinderMaths = Maths;

  // ------------------------------------------------------------------- UI

  if (typeof document === 'undefined') return;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  var STEPS = ['intro', 'corners', 'points', 'result'];

  function SizeFinder(rootEl) {
    this.root = rootEl;
    this.strings = JSON.parse(rootEl.getAttribute('data-strings') || '{}');
    this.img = null;          // working bitmap, capped at LIMITS.workingEdgePx
    this.corners = null;      // in working-bitmap pixels
    this.heel = null;
    this.toe = null;
    this.analysis = null;
    this.results = [];        // one entry per foot measured
    this.drag = null;
    this.mode = 'corners';
    this.build();
  }

  SizeFinder.prototype.t = function (key, fallback) {
    return (this.strings && this.strings[key]) || fallback;
  };

  /* Which sheet the customer says they used. Declared, never inferred: see the
   * note in analyseQuad on why guessing it is a 20% error waiting to happen. */
  SizeFinder.prototype.paperId = function () {
    return (this.paperSel && this.paperSel.value) || 'a4';
  };

  SizeFinder.prototype.build = function () {
    var self = this;
    this.stage = this.root.querySelector('[data-sf-stage]');
    this.canvas = this.root.querySelector('[data-sf-canvas]');
    this.ctx = this.canvas.getContext('2d');
    this.loupe = this.root.querySelector('[data-sf-loupe]');
    this.lctx = this.loupe.getContext('2d');
    this.readout = this.root.querySelector('[data-sf-readout]');
    this.fileInputs = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-sf-file]')
    );
    this.paperSel = this.root.querySelector('[data-sf-paper]');
    this.hint = this.root.querySelector('[data-sf-hint]');
    this.notice = this.root.querySelector('[data-sf-notice]');
    this.result = this.root.querySelector('[data-sf-result]');
    this.primary = this.root.querySelector('[data-sf-primary]');
    this.back = this.root.querySelector('[data-sf-back]');
    this.progress = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-sf-progress] [data-step]')
    );

    /* Two entry points, not one. A bare file input opens a chooser, and on a
     * phone the camera is then one extra tap down a menu, which is the common
     * case here. The capture attribute opens the camera straight away; it is
     * ignored on desktop, where it harmlessly falls back to a file picker. */
    this.fileInputs.forEach(function (input) {
      input.addEventListener('change', function (e) {
        var f = e.target.files && e.target.files[0];
        if (f) self.loadPhoto(f);
      });
    });
    this.primary.addEventListener('click', function () { self.onPrimary(); });
    this.back.addEventListener('click', function () { self.onBack(); });

    this.canvas.addEventListener('pointerdown', function (e) { self.onDown(e); });
    this.canvas.addEventListener('pointermove', function (e) { self.onMove(e); });
    this.canvas.addEventListener('pointerup', function (e) { self.onUp(e); });
    this.canvas.addEventListener('pointercancel', function (e) { self.onUp(e); });

    this.show('intro');
    window.addEventListener('resize', function () { self.fit(); self.draw(); });
  };

  SizeFinder.prototype.show = function (step) {
    this.step = step;
    this.root.querySelectorAll('[data-sf-step]').forEach(function (n) {
      n.hidden = n.getAttribute('data-sf-step') !== step;
    });
    this.root.setAttribute('data-current', step);
    this.updateProgress();
  };

  /* The progress rail is the only thing telling the customer how much is left.
   * Without it the tool is a sequence of unlabelled screens. */
  SizeFinder.prototype.updateProgress = function () {
    var name = this.step === 'work' ? this.mode : this.step;
    var at = STEPS.indexOf(name);
    this.progress.forEach(function (n) {
      var i = STEPS.indexOf(n.getAttribute('data-step'));
      n.setAttribute('data-state', i < at ? 'done' : (i === at ? 'now' : 'todo'));
    });
  };

  SizeFinder.prototype.say = function (msg, kind) {
    this.notice.textContent = msg || '';
    this.notice.hidden = !msg;
    this.notice.setAttribute('data-kind', kind || 'info');
  };

  /*
   * Decode at a capped size. A 12 MP photo is 12 million pixels; iOS silently
   * returns a blank canvas above its area limit rather than throwing, which
   * would surface as "no paper found" instead of as an error. 2400 px on the
   * long edge still leaves about 67 px per cm across a sheet of A4, far finer
   * than the measurement needs.
   */
  SizeFinder.prototype.loadPhoto = function (file) {
    var self = this;
    this.say('');
    var done = function (bmp) {
      var scale = Math.min(1, LIMITS.workingEdgePx / Math.max(bmp.width, bmp.height));
      var w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
      var off = document.createElement('canvas');
      off.width = w; off.height = h;
      off.getContext('2d').drawImage(bmp, 0, 0, w, h);
      if (bmp.close) bmp.close();
      self.img = off;
      self.corners = self.defaultCorners(w, h);
      self.heel = null; self.toe = null; self.analysis = null;
      self.mode = 'corners';
      self.show('work');
      self.setStepCopy();
      self.fit();
      self.draw();
    };

    if (typeof createImageBitmap === 'function') {
      // from-image applies the EXIF rotation that phone cameras write.
      createImageBitmap(file, { imageOrientation: 'from-image' })
        .then(done)
        .catch(function () { self.loadViaImageEl(file, done); });
    } else {
      this.loadViaImageEl(file, done);
    }
  };

  SizeFinder.prototype.loadViaImageEl = function (file, done) {
    var self = this;
    var url = URL.createObjectURL(file);
    var im = new Image();
    im.onload = function () { done(im); URL.revokeObjectURL(url); };
    im.onerror = function () {
      URL.revokeObjectURL(url);
      self.say(self.t('err_photo', 'That photo could not be opened. Please try another.'), 'error');
    };
    im.src = url;
  };

  SizeFinder.prototype.setStepCopy = function () {
    if (this.mode === 'corners') {
      this.hint.textContent = this.t('hint_corners',
        'Drag each dot onto a corner of the paper.');
      this.primary.textContent = this.t('cta_corners', 'Next');
    } else {
      this.hint.textContent = this.t('hint_points',
        'Drag one dot to the back of the heel, the other to the tip of the longest toe.');
      this.primary.textContent = this.t('cta_measure', 'Get my size');
    }
    this.updateProgress();
  };

  SizeFinder.prototype.defaultCorners = function (w, h) {
    var mx = w * 0.18, my = h * 0.12;
    return [[mx, my], [w - mx, my], [w - mx, h - my], [mx, h - my]];
  };

  SizeFinder.prototype.fit = function () {
    if (!this.img) return;
    var avail = this.stage.clientWidth || 320;
    // Leave room for the hint and the action bar so Next is never below the
    // fold: on a phone the first build pushed it off-screen behind a tall photo.
    var maxH = Math.max(260, Math.min(window.innerHeight * 0.46, 680));
    var s = Math.min(avail / this.img.width, maxH / this.img.height);
    this.scale = s;
    this.canvas.width = Math.round(this.img.width * s);
    this.canvas.height = Math.round(this.img.height * s);
    this.canvas.style.width = this.canvas.width + 'px';
    this.canvas.style.height = this.canvas.height + 'px';
  };

  SizeFinder.prototype.toDisplay = function (p) {
    return [p[0] * this.scale, p[1] * this.scale];
  };

  SizeFinder.prototype.handles = function () {
    if (this.mode === 'corners') return this.corners;
    var out = [];
    if (this.heel) out.push(this.heel);
    if (this.toe) out.push(this.toe);
    return out;
  };

  SizeFinder.prototype.tracePath = function (ctx) {
    var s = this.scale;
    ctx.beginPath();
    this.corners.forEach(function (p, i) {
      var x = p[0] * s, y = p[1] * s;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.closePath();
  };

  SizeFinder.prototype.draw = function () {
    if (!this.img) return;
    var self = this, ctx = this.ctx, s = this.scale;
    var W = this.canvas.width, H = this.canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(this.img, 0, 0, W, H);

    if (this.mode === 'corners') {
      /* Dim everything outside the quad. This is what makes corner placement
       * legible on a cluttered photo: the sheet is the only bright thing, so
       * an error is obvious at a glance rather than only after a refusal. */
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, W, H);
      this.corners.forEach(function (p, i) {
        var x = p[0] * s, y = p[1] * s;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fillStyle = 'rgba(8, 10, 16, 0.62)';
      ctx.fill('evenodd');
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.lineWidth = 2;
      ctx.setLineDash([7, 5]);
      this.tracePath(ctx);
      ctx.stroke();
      ctx.restore();

      // forEach passes (value, index, ARRAY). Binding dot directly here handed
      // the whole corner array in as the label, and canvas dutifully painted
      // every coordinate across the photo.
      this.corners.forEach(function (p, i) { self.dot(p, i); });
      this.showReadout(null);
    } else {
      if (this.heel && this.toe) {
        var a = this.toDisplay(this.heel), b = this.toDisplay(this.toe);
        ctx.save();
        ctx.strokeStyle = 'rgba(0,0,0,0.55)';
        ctx.lineWidth = 6;
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
        ctx.restore();
      }
      var heelBelow = false, toeBelow = true;
      if (this.heel && this.toe) {
        // Whichever point is higher keeps its label above; the lower one puts
        // its label underneath, so they cannot meet in the middle.
        heelBelow = this.heel[1] > this.toe[1];
        toeBelow = !heelBelow;
      }
      if (this.heel) this.dot(this.heel, 0, this.t('label_heel', 'HEEL'), heelBelow);
      if (this.toe) this.dot(this.toe, 1, this.t('label_toe', 'TOE'), toeBelow);
      this.showReadout(this.liveLength());
    }
  };

  /* Length under the current handles, so the number moves as the customer
   * drags rather than appearing only after they commit. */
  SizeFinder.prototype.liveLength = function () {
    if (!this.analysis || !this.heel || !this.toe) return null;
    var H = measurementHomography(this.analysis);
    if (!H) return null;
    var a = applyH(H, this.heel), b = applyH(H, this.toe);
    if (!a || !b) return null;
    return dist(a, b);
  };

  SizeFinder.prototype.showReadout = function (cm) {
    if (!this.readout) return;
    if (cm == null || !isFinite(cm)) { this.readout.hidden = true; return; }
    this.readout.textContent = cm.toFixed(1) + ' cm';
    this.readout.hidden = false;
  };

  SizeFinder.prototype.dot = function (p, i, label, below) {
    var ctx = this.ctx, s = this.scale;
    var x = p[0] * s, y = p[1] * s;
    var R = 15;
    ctx.save();
    if (label) {
      ctx.font = '700 10px ui-sans-serif, system-ui, sans-serif';
      var w = ctx.measureText(label).width + 14;
      var top = below ? y + R + 6 : y - R - 24;
      ctx.fillStyle = 'rgba(8,10,16,0.82)';
      if (ctx.roundRect) {
        ctx.beginPath(); ctx.roundRect(x - w / 2, top, w, 18, 9); ctx.fill();
      } else {
        ctx.fillRect(x - w / 2, top, w, 18);
      }
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x, top + 9);
    }
    ctx.beginPath();
    ctx.arc(x, y, R, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(8,10,16,0.45)';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x - 6, y); ctx.lineTo(x + 6, y);
    ctx.moveTo(x, y - 6); ctx.lineTo(x, y + 6);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, y, 2, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.restore();
  };

  SizeFinder.prototype.evtPoint = function (e) {
    var r = this.canvas.getBoundingClientRect();
    return [(e.clientX - r.left) / this.scale, (e.clientY - r.top) / this.scale];
  };

  SizeFinder.prototype.onDown = function (e) {
    if (!this.img) return;
    var p = this.evtPoint(e);
    var hs = this.handles();
    var best = -1, bestD = Infinity;
    for (var i = 0; i < hs.length; i++) {
      var d = dist(p, hs[i]);
      if (d < bestD) { bestD = d; best = i; }
    }
    // 44 CSS px is the usual minimum touch target; convert to image pixels.
    var grab = 44 / this.scale;
    if (best < 0 || bestD > grab) return;

    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    this.drag = { index: best, grabOffset: [hs[best][0] - p[0], hs[best][1] - p[1]] };
    this.showLoupe(hs[best]);
    this.draw();
  };

  SizeFinder.prototype.onMove = function (e) {
    if (!this.drag) return;
    e.preventDefault();
    var p = this.evtPoint(e);
    var np = [
      Math.max(0, Math.min(this.img.width, p[0] + this.drag.grabOffset[0])),
      Math.max(0, Math.min(this.img.height, p[1] + this.drag.grabOffset[1]))
    ];
    if (this.mode === 'corners') this.corners[this.drag.index] = np;
    else if (this.drag.index === 0) this.heel = np;
    else this.toe = np;
    this.showLoupe(np);
    this.draw();
  };

  SizeFinder.prototype.onUp = function (e) {
    if (!this.drag) return;
    try { this.canvas.releasePointerCapture(e.pointerId); } catch (err) {}
    this.drag = null;
    this.hideLoupe();
    this.draw();
  };

  /* A magnifier is what makes tap precision achievable at all: on a phone one
   * display pixel can be ten pixels of the photograph. */
  SizeFinder.prototype.showLoupe = function (p) {
    var Z = 4, R = 56;
    this.loupe.width = R * 2; this.loupe.height = R * 2;
    var ctx = this.lctx;
    ctx.clearRect(0, 0, R * 2, R * 2);
    ctx.save();
    ctx.beginPath();
    ctx.arc(R, R, R - 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, R * 2, R * 2);
    var src = R / Z;
    ctx.drawImage(this.img, p[0] - src, p[1] - src, src * 2, src * 2, 0, 0, R * 2, R * 2);
    ctx.restore();
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(R - 12, R); ctx.lineTo(R + 12, R);
    ctx.moveTo(R, R - 12); ctx.lineTo(R, R + 12);
    ctx.stroke();
    this.loupe.hidden = false;

    // Keep the magnifier away from the finger: opposite corner, vertically too.
    var d = this.toDisplay(p);
    var left = d[0] < this.canvas.width / 2 ? this.canvas.width - R * 2 - 10 : 10;
    var top = d[1] < this.canvas.height / 2 ? this.canvas.height - R * 2 - 10 : 10;
    this.loupe.style.left = Math.max(0, left) + 'px';
    this.loupe.style.top = Math.max(0, top) + 'px';
  };

  SizeFinder.prototype.hideLoupe = function () { this.loupe.hidden = true; };

  SizeFinder.prototype.onBack = function () {
    if (this.mode === 'points') {
      this.mode = 'corners';
      this.setStepCopy();
      this.say('');
      this.draw();
      return;
    }
    this.show('intro');
    this.say('');
  };

  SizeFinder.prototype.onPrimary = function () {
    if (this.mode === 'corners') return this.confirmCorners();
    return this.finish();
  };

  SizeFinder.prototype.confirmCorners = function () {
    var a = analyseQuad(this.corners, this.img.width, this.img.height, this.paperId());
    if (!a.ok) {
      this.say(this.explain(a.reason, a), 'error');
      return;
    }
    this.analysis = a;
    if (a.tiltDeg > LIMITS.maxTiltDeg) {
      this.say(this.t('err_tilt',
        'The photo is taken at too much of an angle. Hold the phone flat, directly above the sheet, and take it again.'), 'error');
      return;
    }

    var msg = [];
    if (a.paper.id !== 'a4') {
      msg.push(this.t('note_paper', 'Measuring against {paper}.')
        .replace('{paper}', a.paper.label));
    }
    if (a.tiltDeg > 20) {
      msg.push(this.t('note_tilt', 'Slight angle detected. A flatter photo is more accurate.'));
    }
    this.say(msg.join(' '), 'info');

    /*
     * Seed the heel and toe along the sheet's long axis so there is something
     * to drag rather than a blank instruction.
     *
     * TOE goes at the far end of the sheet and HEEL at the near end, because
     * that is how the photograph is actually taken: you stand over the sheet
     * and shoot downwards, so your toes point away from you (top of the frame)
     * and your heel is nearest the camera (bottom). Seeding them the other way
     * round made every customer drag the two dots across each other before
     * they could start. It changes no result, since the measurement is just the
     * distance between them, but it decides whether the first thing the tool
     * asks you to do is undo its own guess.
     */
    var inv = inverseMeasurementHomography(a);
    var p = a.paper;
    var w = a.portrait ? p.short : p.long;
    var h = a.portrait ? p.long : p.short;
    var far = applyH(inv, a.portrait ? [w / 2, h * 0.22] : [w * 0.22, h / 2]);
    var near = applyH(inv, a.portrait ? [w / 2, h * 0.78] : [w * 0.78, h / 2]);
    this.toe = far; this.heel = near;

    this.mode = 'points';
    this.setStepCopy();
    this.draw();
  };

  SizeFinder.prototype.explain = function (reason, a) {
    var map = {
      need_four_corners: 'Please place all four corners.',
      not_convex: 'Those corners cross over each other. Place one on each corner of the sheet, going around it.',
      degenerate: 'Those corners do not form a sheet. Please adjust them.',
      not_a_rectangle: 'Those four points cannot be the corners of a rectangular sheet. Please check each one.',
      paper_mismatch: 'The shape of that sheet does not match the paper size selected. Please check the four corners, and check the paper size above.',
      implausible_length: 'That measurement is not a plausible foot length. Please check the heel and toe points.',
      below_range: 'That is smaller than our smallest size.',
      above_range: 'That is larger than our largest size.'
    };
    return this.t('err_' + reason, map[reason] || 'Something did not look right. Please try again.');
  };

  SizeFinder.prototype.finish = function () {
    var m = measure(this.corners, this.heel, this.toe, this.img.width, this.img.height, this.paperId());
    if (!m.ok) {
      this.say(this.explain(m.reason, m), 'error');
      return;
    }
    var r = recommend(m.cm);
    this.results.push({ cm: m.cm, rec: r, warnings: m.warnings });
    this.say('');
    this.renderResult();
    this.show('result');
  };

  SizeFinder.prototype.renderResult = function () {
    var self = this;
    // Shoes have to fit the larger foot, so the biggest measurement wins.
    var best = this.results.reduce(function (a, b) { return b.cm > a.cm ? b : a; });
    var r = best.rec;
    var box = this.result;
    box.innerHTML = '';

    var card = el('div', 'sf-card');
    card.appendChild(el('p', 'sf-card__len',
      best.cm.toFixed(1) + ' cm' +
      (this.results.length > 1 ? ' ' + this.t('label_larger', '(larger foot)') : '')));

    if (!r.ok) {
      card.appendChild(el('p', 'sf-card__size', '--'));
      card.appendChild(el('p', 'sf-card__note', this.explain(r.reason, r) + ' ' +
        this.t('note_out_of_range', 'Please call 03454156533 and we will help.')));
    } else {
      card.appendChild(el('p', 'sf-card__label', this.t('label_your_size', 'Your size')));
      card.appendChild(el('p', 'sf-card__size', String(r.size)));
      card.appendChild(el('p', 'sf-card__eu',
        this.t('label_eu', 'US / PAK  .  Euro {n}').replace('{n}', r.eu)));
      if (r.between) {
        card.appendChild(el('p', 'sf-card__note', this.t('note_between',
          'You are close to the line between {a} and {b}. We have recommended the larger, which is what we advise when a foot falls between two sizes.')
          .replace('{a}', r.lower.size).replace('{b}', r.upper.size)));
      }
    }
    box.appendChild(card);

    if (best.warnings && best.warnings.length) {
      var w = [];
      if (best.warnings.indexOf('tilt') >= 0) w.push(this.t('warn_tilt', 'the photo was taken at an angle'));
      if (best.warnings.indexOf('off_sheet') >= 0) w.push(this.t('warn_off_sheet', 'a point sits outside the sheet'));
      if (best.warnings.indexOf('corner_precision') >= 0) w.push(this.t('warn_corners', 'the corners look imprecise'));
      if (w.length) {
        box.appendChild(el('p', 'sf-warn',
          this.t('warn_prefix', 'Worth re-checking, because ') + w.join(' and ') + '.'));
      }
    }

    var actions = el('div', 'sf-bar sf-bar--result');
    if (this.results.length === 1) {
      var again = el('button', 'sf-btn sf-btn--ghost',
        this.t('cta_other_foot', 'Measure the other foot'));
      again.type = 'button';
      again.addEventListener('click', function () {
        self.show('intro');
        self.say(self.t('note_other_foot', 'Now trace and photograph the other foot.'), 'info');
      });
      actions.appendChild(again);
    }
    var over = el('button', 'sf-btn sf-btn--ghost', this.t('cta_restart', 'Start again'));
    over.type = 'button';
    over.addEventListener('click', function () {
      self.results = [];
      self.img = null;
      // Clear both inputs, or re-choosing the same file fires no change event.
      self.fileInputs.forEach(function (i) { i.value = ''; });
      self.say('');
      self.show('intro');
    });
    actions.appendChild(over);
    box.appendChild(actions);

    /* Phase 3 hook: when opened from a product page, this is where the
     * recommended size gets written back into the variant picker, and where an
     * out-of-stock size is handed to /pages/pre-order. Deliberately not wired
     * yet, so nothing depends on it before it is tested. */
    this.root.dispatchEvent(new CustomEvent('sizefinder:result', {
      bubbles: true,
      detail: { cm: best.cm, size: r.ok ? r.size : null, between: !!r.between }
    }));
  };

  function init() {
    document.querySelectorAll('[data-size-finder]').forEach(function (n) {
      if (!n.__sf) n.__sf = new SizeFinder(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  root.SizeFinder = SizeFinder;
})(typeof window !== 'undefined' ? window : globalThis);
